import { Box, Grid, IconButton, Paper, Stack, Typography } from '@mui/material'
import { StrategiesValue } from '../../../util/strategyBuilder'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import CustomStrategyView from './CustomStrategyView'
import { useStyles } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { selectStrategyTemplates } from '../../../selectors/strategyTemplate'
import { parseStrategyTemplateOrders, strategyParser } from '../../strategyTemplate/StrategyParser'
import useSelectPrimaryOrUnderlyingSecurity from '../../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { selectUnderlyingOptionsTradeData } from '../../../selectors/options'
import { StrategyTemplateProto, StrategyTemplateProto_StrategyTemplateGroup } from '../../../proto/generated/strategy'
import { deleteStrategyTemplate } from '../../../services/apis/strategyTemplate'
import BinIcon from '../../../assets/icons/BinIcon'
import Edit from '../../../assets/icons/drawingTools/edit'
import { setOpenStrategyTemplateDialog, setStrategyTemplateEditId } from '../../../reducer/strategyTemplates.reducer'
import ReusableDialog from '../../common/reusableDialog'
import { OptionChainViewKey, STRATEGY_LOGOS_BASE_URL } from '../../../assets/constants'
import { CategoryGroupNameToCode, StrategyGroup } from '../../strategyTemplate/StrategyTemplate.types'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { generatePath, useNavigate } from 'react-router-dom'
import { exchangeToJSON } from '../../../proto/generated/common'
import ROUTES from '../../../assets/routes'
import { setPredefinedStrategy, setStrategyGroup, setViewTabs, updateTradeOrders } from '../../../reducer/strategyBuilder.reducer'
import commonActions from '../../../actions/common.actions'
import { WidgetListItems } from '../../../util/sidebar'
import { selectOrderType } from '../../../selectors/strategyBuilder'

export type TileProps = {
    strategy: any
    onStrategyClick: (strategy: any) => void
    loading: boolean
    strategyData?: StrategyTemplateProto
    setDeleteTemplate?: React.Dispatch<React.SetStateAction<StrategyTemplateProto | undefined>>
}

const PredefinedStrategyTiles = ({ optionChainViewKey }: { optionChainViewKey: OptionChainViewKey }) => {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const orderType = useSelector(selectOrderType)
    const [deleteTemplate, setDeleteTemplate] = useState<StrategyTemplateProto>()
    const strategyTemplates = useSelector(selectStrategyTemplates)
    const groupToStrategiesNew: Record<StrategyTemplateProto_StrategyTemplateGroup, StrategyTemplateProto[]> = useMemo(() => {
        const templateMap: Record<StrategyTemplateProto_StrategyTemplateGroup, StrategyTemplateProto[]> = {} as any
        strategyTemplates.forEach((s) => {
            if (templateMap[s.group]) {
                templateMap[s.group].push(s)
            } else {
                templateMap[s.group] = [s]
            }
        })
        return templateMap
    }, [strategyTemplates])

    const { primaryOrUnderlying: mainSecurity, primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const [underlyingSec, setUnderlyingSec] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (underlyingToken) getSecurityAsync(underlyingToken).then(setUnderlyingSec); else setUnderlyingSec(undefined) }, [underlyingToken])

    const handleCustomStrategyTemplateClick = useCallback(
        async (template: StrategyTemplateProto) => {
            const templateSecurityKey = getSecurityKey(template.security?.exchange ?? 0, template.security?.code ?? '')
            const templateSecurity = await getSecurityAsync(templateSecurityKey)
            if (!templateSecurity || !templateSecurity.displayName) {
                if (optionChainViewKey === OptionChainViewKey.LIVE) {
                    const tradeOrders = await parseStrategyTemplateOrders(template, underlyingSec!, optionExpiries!, orderType)
                    commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                    dispatch(setPredefinedStrategy(template.name))
                    dispatch(setStrategyGroup(`${underlyingSec.displayName} - ${template.name}`))
                    dispatch(updateTradeOrders(tradeOrders));
                    dispatch(setViewTabs(true));
                    return
                } else {
                    await strategyParser(template, underlyingSec!, optionExpiries!, optionChainViewKey)
                }
            }
            if (template.security && templateSecurity !== underlyingSec) {
                const message = `Selected Security is "${underlyingSec?.displayName}".\nSelected template is saved for "${templateSecurity?.displayName}".\nDo you wish to change the security to "${templateSecurity?.displayName}"?`
                if (window.confirm(message)) {
                    navigate(
                        generatePath(ROUTES.strategyBuilder, {
                            exchange: exchangeToJSON(template.security?.exchange),
                            symbol: template.security?.code
                        })
                    )
                    if (optionChainViewKey === OptionChainViewKey.LIVE) {
                        const tradeOrders = await parseStrategyTemplateOrders(template, templateSecurity, [], orderType)
                        commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                        dispatch(setStrategyGroup(`${templateSecurity.displayName} - ${template.name}`))
                        dispatch(updateTradeOrders(tradeOrders));
                        dispatch(setViewTabs(true));
                        return
                    } else {
                        await strategyParser(template, templateSecurity, [], optionChainViewKey)
                    }
                }
                return
            } else {
                if (optionChainViewKey === OptionChainViewKey.LIVE) {
                    const tradeOrders = await parseStrategyTemplateOrders(template, mainSecurity!, optionExpiries!, orderType)
                    commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                    dispatch(setStrategyGroup(`${mainSecurity!.displayName} - ${template.name}`))
                    dispatch(updateTradeOrders(tradeOrders));
                    dispatch(setViewTabs(true));
                    return;
                } else {
                    await strategyParser(template, mainSecurity!, optionExpiries!, optionChainViewKey)
                }
            }
        },
        [dispatch,underlyingSec, optionExpiries, optionChainViewKey, navigate, mainSecurity, orderType]
    )

    const onDeleteStrategyTemplate = useCallback(() => {
        if (deleteTemplate) {
            deleteStrategyTemplate({ request: deleteTemplate })
            setDeleteTemplate(undefined)
        }
    }, [deleteTemplate])

    return (
        <Stack height={"100%"}>
            <Paper>
                <CustomStrategyView />
            </Paper>
            <Box className={classes.predefinedStrategiesContainer}>
                <Stack direction={'row'} alignItems={'center'} sx={{ position: 'sticky', top: 0 }}>
                    {Object.keys(groupToStrategiesNew).map((groupCode) => {
                        return (
                            <Typography variant="body1" align={'center'} sx={{ padding: 0.5, width: '100%' }} key={`header-${CategoryGroupNameToCode[groupCode]}`}>
                                {CategoryGroupNameToCode[groupCode]}
                            </Typography>
                        )
                    })}
                </Stack>
                <Box className={classes.strategiesContainer}>
                    <Box className={classes.strategyGroupContainer}>
                        {Object.keys(CategoryGroupNameToCode).map((groupCode) => {
                            const strategies = groupToStrategiesNew?.[Number(groupCode) as StrategyTemplateProto_StrategyTemplateGroup]
                            return strategies ? (
                                <Grid key={groupCode} container className={classes.strategyGroup}>
                                    {strategies.map((strategy) => {
                                        return (
                                            <StrategyTile
                                                key={strategy.id}
                                                strategyData={strategy}
                                                setDeleteTemplate={setDeleteTemplate}
                                                strategy={{
                                                    label: strategy.name,
                                                    description: strategy.description
                                                }}
                                                onStrategyClick={() => handleCustomStrategyTemplateClick(strategy)}
                                                loading={false}
                                            />
                                        )
                                    })}
                                </Grid>
                            ) : null
                        })}
                        <ReusableDialog
                            open={!!deleteTemplate}
                            onClose={() => {
                                setDeleteTemplate(undefined)
                            }}
                            submitText={'Delete'}
                            onSubmit={onDeleteStrategyTemplate}
                            content={`Do you want to delete template '${deleteTemplate?.name ?? ''}'?`}
                        />
                    </Box>
                </Box>
            </Box>
        </Stack>
    )
}

export default memo(PredefinedStrategyTiles)

const StrategyTile: React.FC<TileProps> = memo(({ strategy, onStrategyClick, loading, strategyData, setDeleteTemplate }: TileProps) => {
    const { classes, cx } = useStyles()
    const dispatch = useDispatch()
    const isIconAvailable = useMemo(() => {
        return !!strategyData?.imageUrl
    }, [strategyData?.imageUrl])

    const custom = useMemo(() => {
        return strategyData?.group === StrategyTemplateProto_StrategyTemplateGroup.CUSTOM
    }, [strategyData?.group])

    return (
        <Paper
            className={loading ? cx(classes.strategyTile, classes.strategyTileLoading) : classes.strategyTile}
            data-event={'Strategy Selected'}
            data-event-source={'strategy-builder-cards'}
            data-event-strategy-name={strategy.label}
            onClick={() => onStrategyClick(strategy.label)}
            key={strategy.label}
        >
            {isIconAvailable && (
                <div style={{ width: '30px', display: 'flex', alignItems: 'flex-start' }}>
                    <img src={`${STRATEGY_LOGOS_BASE_URL}${strategyData?.imageUrl ?? ''}.svg`} style={{ opacity: 0.8 }} width={30} height={30} alt="i" />
                </div>
            )}
            <Box className={classes.strategyDetails}>
                <Typography variant="subtitle1" className={classes.strategyLabel}>
                    {strategy.label}
                </Typography>
                <Typography variant="body2" className={classes.strategyDescription} color={'text.secondary'}>
                    {strategy.description}
                </Typography>
                <Stack
                    direction={'row'}
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 1
                    }}
                >
                    {custom ? (
                        <IconButton
                            sx={{ visibility: 'hidden' }}
                            onClick={() => {
                                if (setDeleteTemplate && strategyData) setDeleteTemplate(strategyData)
                            }}
                        >
                            <BinIcon />
                        </IconButton>
                    ) : null}
                    <IconButton
                        sx={{ width: '40px', height: '40px', visibility: 'hidden' }}
                        onClick={() => {
                            dispatch(setStrategyTemplateEditId(strategyData?.id ?? ''))
                            dispatch(setOpenStrategyTemplateDialog(true))
                        }}
                    >
                        <Edit />
                    </IconButton>
                </Stack>
            </Box>
        </Paper>
    )
})
